import * as Sentry from '@sentry/react';

/** Mirrors backend logging_config level numbers (TRACE=5 … CRITICAL=50). */
const LEVEL_NUM = {
  trace: 5,
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
  critical: 50,
};

function parseLevelName(name, fallback) {
  const key = String(name || fallback).toLowerCase();
  return LEVEL_NUM[key] ?? LEVEL_NUM.info;
}

// VITE_* client env vars mirror backend LOG_LEVEL / SENTRY_LOGS_LEVEL.
const consoleMinLevel = parseLevelName(
  import.meta.env.VITE_LOG_LEVEL,
  import.meta.env.DEV ? 'debug' : 'info',
);
const sentryMinLevel = parseLevelName(import.meta.env.VITE_SENTRY_LOGS_LEVEL, 'info');

export function isConsoleLevelEnabled(level) {
  return LEVEL_NUM[level] >= consoleMinLevel;
}

export function isSentryLogLevelEnabled(level) {
  return LEVEL_NUM[level] >= sentryMinLevel;
}

/** Structured context for admin panel logs — filterable in Sentry by admin_tab. */
export function adminLogContext(adminTab, extra = {}) {
  return { view: 'admin', admin_tab: adminTab, ...extra };
}

function toSentryAttributes(context, scope) {
  const attrs = {};
  if (scope) attrs.logger_scope = scope;
  if (!context || typeof context !== 'object') {
    return Object.keys(attrs).length > 0 ? attrs : undefined;
  }
  for (const [key, value] of Object.entries(context)) {
    if (key.startsWith('_') || value === undefined || value === null) continue;
    if (value instanceof Error) {
      attrs[key] = value.message;
      continue;
    }
    if (typeof value === 'object') {
      try {
        attrs[key] = JSON.stringify(value);
      } catch {
        attrs[key] = String(value);
      }
      continue;
    }
    attrs[key] = String(value);
  }
  return Object.keys(attrs).length > 0 ? attrs : undefined;
}

function buildCaptureTags(scope, context) {
  const tags = {};
  if (scope) tags.logger_scope = scope;
  if (context?.view) tags.view = String(context.view);
  if (context?.admin_tab) tags.admin_tab = String(context.admin_tab);
  if (context?.endpoint) tags.api_endpoint = String(context.endpoint);
  if (context?.api_endpoint) tags.api_endpoint = String(context.api_endpoint);
  if (context?.action) tags.action = String(context.action);
  if (context?.county_slug) tags.county_slug = String(context.county_slug);
  if (context?.task_id) tags.task_id = String(context.task_id);
  return Object.keys(tags).length > 0 ? tags : undefined;
}

function emit(level, scope, message, context, err, { capture = true } = {}) {
  const prefixed = scope ? `${scope}: ${message}` : message;
  const attrs = toSentryAttributes(context, scope);

  if (isConsoleLevelEnabled(level)) {
    const fn =
      level === 'debug'
        ? console.debug
        : level === 'info'
          ? console.info
          : level === 'warn'
            ? console.warn
            : console.error;
    if (err) fn(prefixed, context ?? {}, err);
    else if (context && Object.keys(context).length > 0) fn(prefixed, context);
    else fn(prefixed);
  }

  if (
    import.meta.env.VITE_SENTRY_DSN &&
    isSentryLogLevelEnabled(level) &&
    Sentry.logger?.[level]
  ) {
    Sentry.logger[level](prefixed, attrs);
  }

  if (level === 'error' && capture && !context?._skip_sentry_capture) {
    if (err instanceof Error) {
      Sentry.captureException(err, {
        extra: context,
        tags: buildCaptureTags(scope, context),
      });
    } else if (context instanceof Error) {
      Sentry.captureException(context, {
        tags: buildCaptureTags(scope, {}),
      });
    }
  }
}

function resolveErrorArgs(messageOrContext, contextOrError, maybeError) {
  if (messageOrContext instanceof Error) {
    return {
      message: messageOrContext.message || 'Error',
      context: {},
      err: messageOrContext,
    };
  }
  if (typeof messageOrContext !== 'string') {
    return {
      message: 'Error',
      context: messageOrContext ?? {},
      err: contextOrError instanceof Error ? contextOrError : maybeError,
    };
  }
  if (contextOrError instanceof Error) {
    return { message: messageOrContext, context: {}, err: contextOrError };
  }
  if (maybeError instanceof Error) {
    return { message: messageOrContext, context: contextOrError ?? {}, err: maybeError };
  }
  return { message: messageOrContext, context: contextOrError ?? {}, err: undefined };
}

/**
 * Create a scoped logger. Mirrors backend patterns: searchable context, Sentry Logs integration.
 *
 * Usage:
 *   logger.info('Loaded counties', { result_count: rows.length });
 *   logger.error('Upload failed', { action: 'upload', county_id }, error);
 *   logger.warn('Load failed (API already reported)', { action: 'load' }, error);
 */
export function createLogger(scope) {
  return {
    debug(message, context) {
      emit('debug', scope, message, context);
    },
    info(message, context) {
      emit('info', scope, message, context);
    },
    warn(message, contextOrError, maybeError) {
      const resolved = resolveErrorArgs(message, contextOrError, maybeError);
      emit('warn', scope, resolved.message, resolved.context, resolved.err, { capture: false });
    },
    error(message, contextOrError, maybeError) {
      const resolved = resolveErrorArgs(message, contextOrError, maybeError);
      emit('error', scope, resolved.message, resolved.context, resolved.err);
    },
    /** Log API failures without duplicate Sentry issues (api.js already captures). */
    apiFailure(message, contextOrError, maybeError) {
      const resolved = resolveErrorArgs(message, contextOrError, maybeError);
      emit('warn', scope, resolved.message, {
        ...resolved.context,
        _skip_sentry_capture: true,
      }, resolved.err, { capture: false });
    },
  };
}

export const logger = createLogger('app');
